<template>
  <div id="content" v-show="!loading">
    <popup v-show="hasPopup"
           :popup-type="getPopupType"
           :popup-action="getPopupAction"
           :popup-message="getPopupMessage"
           @popup-action="executePopupAction"></popup>
    <form>
      <div class="inner">
        <p class="paging">
          <router-link
              :to="{ name: 'users' }"
              class="btn btn_back"
              v-translate>Seznam
          </router-link>
        </p>
        <p class="buttons" v-if="!amILoggedUser">
          <a
              v-if="!isActive && $_updateActionAllowed"
              class="btn btn_action btn_grey btn_enable"
              :title="'Aktivovat' | translate"
              @click.prevent.stop="updateUserActive(true)"
              v-translate>Aktivovat
          </a>
          <a
              v-if="isActive && $_updateActionAllowed"
              class="btn btn_action btn_grey btn_disable"
              :title="'Deaktivovat' | translate"
              @click.prevent.stop="updateUserActive(false)"
              v-translate>Deaktivovat
          </a>
          <a
              v-if="passwordEditable"
              class="btn btn_action btn_grey btn_refresh"
              :title="'Vygenerovat nové heslo' | translate"
              @click.prevent.stop="resetUserPassword"
              v-translate>Vygenerovat nové heslo
          </a>
          <a
              v-if="$_deleteActionAllowed"
              class="btn btn_action btn_delete"
              :title="'Smazat' | translate"
              @click.prevent.stop="deleteUser"
              v-translate>Smazat
          </a>
        </p>
        <h1>{{ entity.name }}</h1>
      </div>

      <div class="responsiveTabs">
        <div class="tabs_content">
          <section>
            <div class="left">
              <detail-entity-info :entity="entity"></detail-entity-info>
              <p class="profile_image_box">
                <label
                    for="user_image"
                    :class="['profile_image', {is_image: hasImage}]"
                    :title="'Nahrát fotku' | translate">
                  {{ userInitials }}<img :src="userImage" v-if="hasImage">
                </label>
                <input
                    type="file"
                    accept="image/*"
                    name="user_image"
                    id="user_image"
                    class="input_file"
                    data-multiple-caption="{count} files selected"
                    v-if="uploadReady"
                    @change="onFileChange"
                    ref="user_image"
                    :disabled="disabled"
                />
                <label v-if="$_updateActionAllowed" for="user_image" class="add_profile_image">
                  <span v-translate>Nahrát fotku</span> (150×150px)
                </label>
                <a
                    class="btn btn_action btn_delete"
                    :title="'Smazat' | translate"
                    @click.prevent.stop="deleteImage"
                    v-if="hasImage && !disabled"
                    v-translate>Smazat
                </a>
              </p>
            </div>
            <div class="right">
              <div class="field_left">
                <text-input
                    :required="true"
                    id="name"
                    v-model="entity.name"
                    ref="nameref"
                    :disabled="disabled"
                >
                  <translate class="translate">Příjmení a jméno</translate>
                </text-input>
                <text-input
                    :required="true"
                    id="real-login"
                    v-model="entity.login"
                    ref="realloginref"
                    :disabled="disabled"
                >
                  <translate class="translate">Jméno účtu</translate>
                </text-input>
                <select-box
                    id="operator"
                    v-model="entity.id_operator"
                    :options="operatorOptions"
                    :disabled="true"
                >
                  <translate class="translate">Operátor</translate>
                </select-box>
              </div>
              <div class="field_right">
                <input id="login" name="login" type="text" style="display: none">
                <input id="password" name="password" type="password" style="display: none">
                <text-input
                    :required="true"
                    input-type="password"
                    id="real-password"
                    v-model="password"
                    ref="realpasswordref"
                    @focus="onPasswordFocus"
                    @blur="onPasswordBlur"
                    :disabled="disabled"
                >
                  <translate class="translate">Heslo</translate>
                </text-input>
                <text-input
                    :required="true"
                    id="email"
                    v-model="entity.email"
                    ref="useremail"
                    :disabled="disabled"
                >
                  <translate class="translate">Email</translate>
                </text-input>
                <text-input
                    id="phone"
                    v-model="entity.phone"
                    ref="userphone"
                    :disabled="disabled"
                >
                  <translate class="translate">Telefon</translate>
                </text-input>
              </div>
            </div>
          </section>
          <section v-if="!loading && ($_loggedUser_action_allowed('roleGroups', 'read') || isOperatorRole)">
            <div class="left">
              <h2 v-translate>Nastavení oprávnění</h2>
            </div>
            <div class="right">
              <select-box
                  id="idrolegroup"
                  v-model="entity.id_role_group"
                  :options="roleGroups"
                  :disabled="!isOperatorRole && !$_loggedUser_action_allowed('roleGroups', 'update')"
              >
                <translate class="translate">Skupina oprávnění</translate>
              </select-box>
              <role-groups-select-list
                  v-if="$_loggedUser_action_allowed('roleGroups', 'read')"
                  v-model="entity.roles_override"
                  :is-editable="!!entity.id_role_group
                    && $_loggedUser_action_allowed('roleGroups', 'update')"
                  :is-user="true"
                  :edit-allowed-sections="edit_allowed_section_list"
                  :user-role="entity.role"
                  ref="roleGroupsBasicFormRef"
              />
            </div>
          </section>
          <section class="buttons">
            <p>
              <router-link
                  :to="{ name: 'users' }"
                  class="btn btn_back btn_seznam"
                  v-translate>Seznam
              </router-link>
              <input
                  v-if="!disabled"
                  type="submit"
                  :value="'Uložit' | translate"
                  :class="['btn']"
                  @click.prevent.stop="submitForm()"
              />
            </p>
          </section>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Popup from "@/components/common/Popup";
import DetailEntityInfo from "@/components/common/DetailEntityInfo";
import RoleGroupsSelectList from "@/components/views/roleGroups/forms/RoleGroupsSelectList";

import users from "@/components/views/users/mixins/users";
import message from "@/components/mixins/message";
import userSettings from "@/components/mixins/user-settings";
import popup from "@/components/mixins/popup";
import requiredFields from "@/components/mixins/required-fields";
import {AUTH_UPDATE} from "@/store/mutation-types";

export default {
  name: "UserDetail",
  components: {Popup, RoleGroupsSelectList, DetailEntityInfo},
  mixins: [message, userSettings, requiredFields, users, popup],
  data() {
    return {
      loading: true,
      uploadReady: true,
      messageType: 'sendEmail',
      oldEmail: null,
    };
  },
  created(UserService, OperatorService, RoleGroupsService) {
    this.userService = UserService;
    this.operatorService = OperatorService;
    this.roleGroupsService = RoleGroupsService;

    this.getOperator();
    this.getRoleGroupsSimpleList();
    this.getEditAllowedSectionList();
    this.getUser()
        .then(() => {
          this.oldEmail = this.entity.email;
          this.loading = false;
          this.saveLastVisited(this.entity.id);
        })
        .catch(error => {
          this.setErrorMessage(error['error_message']);
        });
  },
  computed: {
    disabled(){
      return !this.$_updateActionAllowed;
    },
    hasImage() {
      return this.selectedFile
          || (this.entity.image !== null
          && this.entity.image !== ''
          && this.entity.image != 'null'
          && this.entity.image != undefined
          && this.entity.image != 'undefined');
    },
    userImage() {
      if (this.selectedFile) {
        return URL.createObjectURL(this.selectedFile);
      }
      return 'uploads/user_images/' + this.entity.image;
    },
    amILoggedUser() {
      return Number(this.entity.id) === Number(this.$store.getters.getLoggedUserId);
    },
    isActive() {
      return this.entity.active;
    },
    passwordEditable() {
      return !this.disabled && this.oldEmail && this.oldEmail.length > 0;
    }
  },
  methods: {
    submitForm(redirect = false) {
      this.updateUser(this.entity, redirect);
    },
    redirect() {
      this.$router.push({name: 'users'});
    },
    executePopupAction(value) {
      if (value === true) {
        if (this.getPopupAction === 'delete') {
          this.deleteUser(false);
        }
        if (this.getPopupAction === 'delete-image') {
          this.deleteImage(false);
        }
        if (this.getPopupAction === 'reset-password') {
          this.resetUserPassword(false);
        }
      }
      this.hidePopup();
    },

    // USER
    getUser(refreshAuthStore = false) {
      this.setLoadingMessage();

      return this.userService.getEntity(this.$route.params.id, {id_selected_operator: this.$_loggedUser_selectedOperatorId})
          .then(data => {
            this.entity = data.user;

            if (this.amILoggedUser && refreshAuthStore) {
              this.$store.dispatch(AUTH_UPDATE, {
                full_name: data.user.name,
                user_name: data.user.login,
                user_role: data.user.role,
                user_image: data.user.image,
                operator_id: data.user.id_operator,
                operator_name: data.user.operator_name,
                logged_operator: this.$store.getters.getLoggedOperator,
                user_role_list: data.user.role_list,
                operator_role_list: data.user.operaotr_role_list,
              });
            }

            this.newPassword = null;
            this.resetPassword();
            this.validateRequiredFields();

            this.emptyMessage();
          })
          .catch(function (error) {
            this.setErrorMessage(error['error_message']);
          }.bind(this));
    },
    updateUserActive(newValue) {
      this.userService.updateEntity(this.entity.id, {active: newValue})
          .then(() => {
            this.setSuccessMessage();
            this.entity.active = newValue;
          })
          .catch(function (error) {
            this.setErrorMessage(error['error_message']);
          }.bind(this));
    },
    updateUser(params, redirect = false) {
      this.userService.updateEntity(this.entity.id, this.filterUserParams({...params}))
          .then(() => {
            this.oldEmail = this.entity.email;
            Promise.resolve(this.uploadImage(this.entity.id))
                .then(() => {
                  this.setSuccessMessage();
                  this.getUser(true);

                  if (redirect === true) {
                    this.redirect();
                  }
                });
          })
          .catch(function (error) {
            this.setErrorMessage(this.processErrorMessage(error));
          }.bind(this));
    },
    deleteUser(showPopup = true) {
      if (showPopup) {
        this.showPopup(null, 'delete', 'delete');
      } else {
        this.userService.deleteEntity(this.entity.id, {id_selected_operator: this.$_loggedUser_selectedOperatorId})
            .then(() => {
              this.$router.push({name: 'users'});
            })
            .catch(function (error) {
              this.setErrorMessage(error['error_message']);
            }.bind(this));
      }
    },
    deleteImage(showPopup = true) {
      if (this.selectedFile) {
        this.selectedFile = null;
        this.uploadReady = false;
        this.$nextTick(() => {
          this.uploadReady = true;
        });
        return null;
      }
      if (showPopup) {
        this.showPopup(null, 'delete-image', 'delete-image');
      } else {
        this.userService.deleteImage(this.entity.id)
            .then(() => {
              this.setSuccessMessage();
              this.getUser(true);
            })
            .catch(function (error) {
              this.setErrorMessage(error['error_message']);
            }.bind(this));
      }
    },
    resetUserPassword(showPopup = true) {
      if (showPopup) {
        this.showPopup(null, 'reset-password', 'reset-password', null, 'Vygenerovat nové heslo a odeslat na e-mail ' + this.oldEmail);
      } else {
        this.userService.resetUserPassword(this.entity.id, { messageType: this.messageType })
            .then(() => {
              this.setSuccessMessage(this.$gettext("Nové heslo bylo úspěšně odesláno na e-mail %{email}", { email: this.entity.email }));
            })
            .catch(error => {
              error['validation_errors'].forEach(error => {
                let errorMessage;

                if (error === "Receiver email address is not defined.") {
                  errorMessage = this.$gettext('Odběratel nemá definovanou adresu, na kterou by mohl být zaslán email s heslem.')
                } else if (error === "Default sender email address is not defined.") {
                  errorMessage = this.$gettext('Není definována kontaktní adresa odesilatele.')
                } else if (this._.includes(error, 'No email template for message type')) {
                  errorMessage = this.$gettext('Není definována šablona emailu pro reset hesla.')
                } else {
                  errorMessage = error;
                }
                this.setErrorMessage(errorMessage);
              });
            })
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/views/users/UserDetail.vue